import { useEffect, useMemo, useRef, useState } from "react";
import { inr, round2 } from "../utils/posBilling";
import { showError } from "../utils/toastUtils";

export const PAYMENT_METHODS = [
  { value: "cash", label: "Cash", icon: "ti ti-cash" },
  { value: "upi", label: "UPI", icon: "ti ti-qrcode" },
  { value: "card", label: "Card", icon: "ti ti-credit-card" },
  { value: "bank", label: "Bank Transfer", icon: "ti ti-building-bank" },
  { value: "other", label: "Other", icon: "ti ti-dots" },
];

export const methodLabel = (m) => PAYMENT_METHODS.find((x) => x.value === m)?.label || m;

const REF_PLACEHOLDER = {
  upi: "UPI transaction ID (optional)",
  card: "Card last 4 digits / approval code (optional)",
  bank: "UTR / reference no. (optional)",
  other: "Reference (optional)",
};

/**
 * onConfirm({ payments:[{method, amount, reference}], cashTendered })
 * total = amount to collect (new bill ka grand total, ya collect-due me due amount)
 */
export default function PaymentModal({
  total,
  title = "Payment",
  confirmLabel = "Confirm & Generate Invoice",
  allowPayLater = true,
  submitting = false,
  onClose,
  onConfirm,
}) {
  const [mode, setMode] = useState("single"); // single | split | later
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [cashTendered, setCashTendered] = useState("");
  const [rows, setRows] = useState([
    { method: "cash", amount: "", reference: "" },
    { method: "upi", amount: "", reference: "" },
  ]);
  const tenderedRef = useRef(null);

  useEffect(() => {
    if (mode === "single" && method === "cash") tenderedRef.current?.focus();
  }, [mode, method]);

  const payments = useMemo(() => {
    if (mode === "later") return [];
    if (mode === "single") return [{ method, amount: total, reference }];
    return rows.filter((r) => Number(r.amount) > 0).map((r) => ({ ...r, amount: Number(r.amount) }));
  }, [mode, method, reference, rows, total]);

  const paid = round2(payments.reduce((s, p) => s + Number(p.amount), 0));
  const due = round2(Math.max(total - paid, 0));
  const cashPart = round2(payments.filter((p) => p.method === "cash").reduce((s, p) => s + Number(p.amount), 0));
  const tendered = Number(cashTendered) || 0;
  const change = tendered > cashPart ? round2(tendered - cashPart) : 0;

  const quickCash = useMemo(() => {
    const base = mode === "single" ? total : cashPart;
    if (!base) return [];
    const set = new Set([base, Math.ceil(base / 100) * 100, Math.ceil(base / 500) * 500, Math.ceil(base / 2000) * 2000]);
    return [...set].filter((v) => v >= base).slice(0, 4);
  }, [mode, total, cashPart]);

  const showCash = (mode === "single" && method === "cash") || (mode === "split" && cashPart > 0);

  const setRow = (i, k, v) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const fillRemaining = (i) => {
    const others = rows.reduce((s, r, idx) => (idx === i ? s : s + (Number(r.amount) || 0)), 0);
    setRow(i, "amount", String(round2(Math.max(total - others, 0))));
  };

  const confirm = () => {
    if (paid > total + 0.009) return showError(`Paid amount cannot exceed ${inr(total)}`);
    if (mode === "split" && paid <= 0) return showError("Enter at least one payment amount");
    if (!allowPayLater && paid <= 0) return showError("Enter an amount to collect");
    if (tendered > 0 && tendered < cashPart) return showError("Cash received is less than the cash amount");

    onConfirm({
      payments: payments.map((p) => ({ method: p.method, amount: round2(p.amount), reference: (p.reference || "").trim() })),
      cashTendered: showCash && tendered > 0 ? tendered : null,
    });
  };

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1} onMouseDown={(e) => e.target === e.currentTarget && !submitting && onClose()}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button className="btn-close" onClick={onClose} disabled={submitting} />
            </div>

            <div className="modal-body">
              <div className="pos-pay-total mb-3">
                <small className="text-muted">Amount to collect</small>
                <div>{inr(total)}</div>
              </div>

              <div className="btn-group w-100 mb-3">
                <button className={`btn btn-outline-primary ${mode === "single" ? "active" : ""}`} onClick={() => setMode("single")}>
                  Full payment
                </button>
                <button className={`btn btn-outline-primary ${mode === "split" ? "active" : ""}`} onClick={() => setMode("split")}>
                  Split / Partial
                </button>
                {allowPayLater && (
                  <button className={`btn btn-outline-primary ${mode === "later" ? "active" : ""}`} onClick={() => setMode("later")}>
                    Pay later
                  </button>
                )}
              </div>

              {/* ---------- SINGLE ---------- */}
              {mode === "single" && (
                <>
                  <div className="pos-pay-methods mb-3">
                    {PAYMENT_METHODS.map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        className={`pos-pay-method ${method === m.value ? "active" : ""}`}
                        onClick={() => setMethod(m.value)}
                      >
                        <i className={m.icon} />
                        <span>{m.label}</span>
                      </button>
                    ))}
                  </div>
                  {method !== "cash" && (
                    <input
                      className="form-control"
                      placeholder={REF_PLACEHOLDER[method]}
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                    />
                  )}
                </>
              )}

              {/* ---------- SPLIT ---------- */}
              {mode === "split" && (
                <>
                  {rows.map((r, i) => (
                    <div className="row g-2 mb-2 align-items-center" key={i}>
                      <div className="col-4">
                        <select className="form-select" value={r.method} onChange={(e) => setRow(i, "method", e.target.value)}>
                          {PAYMENT_METHODS.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-4">
                        <div className="input-group">
                          <input
                            className="form-control"
                            placeholder="Amount"
                            inputMode="decimal"
                            value={r.amount}
                            onChange={(e) => /^\d*(\.\d{0,2})?$/.test(e.target.value) && setRow(i, "amount", e.target.value)}
                          />
                          <button type="button" className="btn btn-outline-secondary" title="Fill remaining" onClick={() => fillRemaining(i)}>
                            <i className="ti ti-arrow-bar-to-left" />
                          </button>
                        </div>
                      </div>
                      <div className="col-3">
                        <input
                          className="form-control"
                          placeholder="Ref."
                          disabled={r.method === "cash"}
                          value={r.method === "cash" ? "" : r.reference}
                          onChange={(e) => setRow(i, "reference", e.target.value)}
                        />
                      </div>
                      <div className="col-1 text-end">
                        {rows.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-link text-danger p-0"
                            onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
                          >
                            <i className="ti ti-trash" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {rows.length < 5 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary mb-2"
                      onClick={() => setRows((prev) => [...prev, { method: "cash", amount: "", reference: "" }])}
                    >
                      + Add payment
                    </button>
                  )}
                </>
              )}

              {mode === "later" && (
                <div className="alert alert-warning mb-0 small">
                  The invoice will be marked <b>Unpaid</b>. You can collect the payment later from Offline Sales.
                </div>
              )}

              {/* ---------- CASH TENDERED ---------- */}
              {showCash && (
                <div className="mt-3">
                  <label className="form-label small mb-1">Cash received from customer</label>
                  <input
                    ref={tenderedRef}
                    className="form-control"
                    inputMode="decimal"
                    placeholder={String(mode === "single" ? total : cashPart)}
                    value={cashTendered}
                    onChange={(e) => /^\d*(\.\d{0,2})?$/.test(e.target.value) && setCashTendered(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && confirm()}
                  />
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {quickCash.map((v) => (
                      <button key={v} type="button" className="btn btn-sm btn-light border" onClick={() => setCashTendered(String(v))}>
                        {inr(v)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="pos-totals mt-3">
                <div><span>Paying now</span><span>{inr(paid)}</span></div>
                {due > 0 && <div className="text-danger"><span>Balance due</span><span>{inr(due)}</span></div>}
                {change > 0 && <div className="grand text-success"><span>Return change</span><span>{inr(change)}</span></div>}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
                Back
              </button>
              <button className="btn btn-primary" onClick={confirm} disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" /> Saving...
                  </>
                ) : (
                  confirmLabel
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
}